import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';

import { CurrencyAutocomplete } from 'components/_basic/CurrencyAutocomplete';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import {
  EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES,
  prefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

export const FinancialInfoExecSum: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = true;

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('executiveSummary.title')}
          badgeText={t('executiveSummary.financialInformation.title')}
          section={FOR_INVESTORS_SECTIONS.EXECUTIVE_SUMMARY}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={6}>
            <CurrencyAutocomplete label={t('selectCurrencyForEntireDocument')} disabled={!canAccess} />
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.averageAnnualIncome"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ANNUAL_INCOME)}
              label={t('executiveSummary.financialInformation.averageAnnualIncome')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.averageAnnualProfit"
              name={concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.AVERAGE_ANNUAL_PROFIT)}
              label={t('executiveSummary.financialInformation.averageAnnualProfit')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.projectedAnnualIncome"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME)}
              label={t('executiveSummary.financialInformation.projectedAnnualIncome')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.projectedAnnualProfit"
              name={concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.PROJECTED_ANNUAL_PROFIT)}
              label={t('executiveSummary.financialInformation.projectedAnnualProfit')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.forecastedProfitability"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY)}
              label={t('executiveSummary.financialInformation.forecastedProfitability')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.projectedPlayback"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD)}
              label={t('executiveSummary.financialInformation.projectedPlayback')}
              disabled={!canAccess}
              maxLength={3}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.returnOnInvestment"
              name={concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.RETURN_ON_INVESTMENT)}
              label={t('executiveSummary.financialInformation.returnOnInvestment')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialInformation.internalRateOfReturn"
              name={concatWithDot(prefix, EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.INTERNAL_RATE_OF_RETURN)}
              label={t('executiveSummary.financialInformation.internalRateOfReturn')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
};
